// Filter op buurt en straat voor het tabblad 'Registratie' van een wervingsactie.
//
// Nodig omdat dat tabblad alle rijen van de actie in één tabel zet. Met een
// hele wijk gekozen zijn dat er duizenden (Zuidwijken: 3.550), terwijl een
// werver straat voor straat werkt. De volgorde komt uit compareAddresses, zodat
// 'Registratie' en 'Gebied' dezelfde volgorde houden.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)

import { compareAddresses } from './groupAddressesByStreet'

// Waarde in het buurtmenu voor rijen zonder buurtcode. Niet '' — dat is 'alle buurten'.
const OVERIG_BUURT = '__overig'
const OVERIG = 'Overig'

/**
 * @typedef {object} RegistratieFilter
 * @property {string} buurt - Buurtcode, OVERIG_BUURT, of '' voor alle buurten
 * @property {string} straat - Straatnaam, 'Overig', of '' voor alle straten
 */

/**
 * @typedef {object} FilterOptie
 * @property {string} value
 * @property {string} label - Naam met het aantal rijen erachter
 */

/**
 * @param {object} r - Wervingsregistratie
 * @returns {string} De waarde in het buurtmenu
 */
const buurtValue = (r) => r.BuurtCode || OVERIG_BUURT

/**
 * @param {object} r - Wervingsregistratie
 * @returns {string} De waarde in het straatmenu
 */
const straatValue = (r) => r.Straatnaam || OVERIG

/**
 * De rijen die bij het filter horen, op straat en huisnummer.
 *
 * @param {Array<object>} registraties
 * @param {RegistratieFilter} filter
 * @returns {Array<object>}
 */
const filterRegistraties = (registraties, { buurt, straat }) => (registraties || [])
    .filter((r) => !buurt || buurtValue(r) === buurt)
    .filter((r) => !straat || straatValue(r) === straat)
    .sort(compareAddresses)

/**
 * @param {Array<object>} rows
 * @param {(r: object) => string} keyOf
 * @returns {Map<string, number>}
 */
const countBy = (rows, keyOf) => {
    const counts = new Map()
    for (const r of rows) counts.set(keyOf(r), (counts.get(keyOf(r)) || 0) + 1)
    return counts
}

/**
 * De keuzes voor het buurt- en het straatmenu. De straten volgen de gekozen buurt.
 *
 * @param {Array<object>} registraties
 * @param {object} context
 * @param {Object<string, string>} context.buurtNames - Buurtcode → naam (mag leeg zijn zolang hij laadt)
 * @param {string} context.buurt - De gekozen buurt, of ''
 * @returns {{ buurten: Array<FilterOptie>, straten: Array<FilterOptie> }}
 */
const registratieFilterOptions = (registraties, { buurtNames, buurt }) => {
    const rows = registraties || []
    const naamVan = (code) => (code === OVERIG_BUURT ? OVERIG : (buurtNames?.[code] || code))

    const buurten = Array.from(countBy(rows, buurtValue).entries())
        .map(([value, count]) => ({ value, naam: naamVan(value), count }))
        .sort((a, b) => {
            if ((a.value === OVERIG_BUURT) !== (b.value === OVERIG_BUURT)) return a.value === OVERIG_BUURT ? 1 : -1
            return a.naam.localeCompare(b.naam, 'nl')
        })
        .map(({ value, naam, count }) => ({ value, label: `${naam} (${count})` }))

    const inBuurt = buurt ? rows.filter((r) => buurtValue(r) === buurt) : rows
    const straten = Array.from(countBy(inBuurt, straatValue).entries())
        .sort(([a], [b]) => a.localeCompare(b, 'nl'))
        .map(([value, count]) => ({ value, label: `${value} (${count})` }))

    return { buurten, straten }
}

export { OVERIG_BUURT, filterRegistraties, registratieFilterOptions }
